import { Agenda } from './agenda';
import { Invitation, restoreInvitation } from './invitation';
import { AccountWorkspace, accountViews } from './account-workspace';
import { CalendarReconnectNotice } from './account-calendars';
import {
  useWorkspaceLocation,
  navigateWorkspace,
  prepareNavigation,
} from './navigation';
import { useEffect, useState, type JSX } from 'react';
import { z } from 'zod';
import {
  ApiError,
  botPath,
  request,
  sessionSchema,
  snapshotSchema,
  providersSchema,
  SIGN_IN_REQUIRED_EVENT,
  type Session,
} from './api';
import { useRemote } from './hooks';
import { translations, type Language } from './copy';
import { ErrorNotice, useMutation } from './components';
import { Inbox } from './inbox';
import { ConfigurationEditor, Knowledge, Connections, Usage } from './settings';
import { Privacy } from './privacy';
import { Billing } from './billing';
import { Confirmation } from './confirmation';
import { AccountVerification } from './account-verification';
import { Quality } from './quality';
import { Readiness } from './readiness';
import { ClientInstallation } from './client-installation';
import { Leads } from './leads';
import { Handoff, HandoffNotice } from './handoff';
import { PlanCapacityNotice } from './plan-capacity';

export type View =
  | 'agenda'
  | 'home'
  | 'assistants'
  | 'accountAiProviders'
  | 'accountTeam'
  | 'help'
  | 'leads'
  | 'inbox'
  | 'configuration'
  | 'knowledge'
  | 'connections'
  | 'usage'
  | 'quality'
  | 'readiness'
  | 'handoffOperations'
  | 'privacy';

function accountDestination(view: View, companyAdmin: boolean): boolean {
  return (
    accountViews.includes(view) &&
    (!['connections', 'usage', 'privacy'].includes(view) || companyAdmin)
  );
}

export function App(): JSX.Element {
  restoreInvitation();
  const [creatingClient, setCreatingClient] = useState(false);
  const [language, setLanguage] = useState<Language>('es');
  const copy = translations[language];
  const publicJourney = ['/confirm', '/verify-account'].includes(
    window.location.pathname
  );
  const session = useRemote(
    publicJourney ? null : '/v1/session',
    sessionSchema
  );
  const invalidateSession = session.invalidate;
  useEffect(() => {
    const requireSignIn = () => {
      invalidateSession(new ApiError('SIGN_IN_REQUIRED', 401));
      setCreatingClient(false);
    };
    window.addEventListener(SIGN_IN_REQUIRED_EVENT, requireSignIn);
    return () =>
      window.removeEventListener(SIGN_IN_REQUIRED_EVENT, requireSignIn);
  }, [invalidateSession]);
  const providers = useRemote(
    publicJourney ? null : '/v1/providers',
    providersSchema
  );
  const location = useWorkspaceLocation();
  const selected = location.searchParams.get('botId') ?? '';
  const views: View[] = [
    'home',
    'agenda',
    'assistants',
    'inbox',
    'leads',
    'configuration',
    'knowledge',
    'connections',
    'usage',
    'quality',
    'readiness',
    'handoffOperations',
    'privacy',
    'accountAiProviders',
    'accountTeam',
    'help',
  ];
  const view: View =
    views.find((item) => item === location.searchParams.get('view')) ?? 'home';
  function setView(value: View) {
    const isAccount = accountDestination(value, Boolean(accountAdmin));
    const companyId = isAccount
      ? accountAdmin
        ? accountCompanyId
        : (session.data?.accounts.find((account) =>
            ['owner', 'admin'].includes(account.role)
          )?.companyId ?? null)
      : (member?.company_id ?? null);
    void mutation.run(() =>
      navigateWorkspace({
        view: value,
        companyId,
        botId: isAccount
          ? companyId === accountCompanyId
            ? selected || null
            : null
          : selectedBotId || null,
        step: null,
      })
    );
  }
  function setSelected(value: string) {
    const selection = session.data?.memberships.find(
      (item) => item.bot_id === value
    );
    void mutation.run(() =>
      navigateWorkspace({
        botId: value,
        companyId: selection?.company_id ?? null,
        step: null,
      })
    );
  }
  const mutation = useMutation();
  if (window.location.pathname === '/invite')
    return (
      <Invitation
        session={session.data}
        providers={providers.data?.providers ?? []}
        copy={copy}
        language={language}
        reloadSession={session.reload}
        authenticationLoading={session.loading || providers.loading}
        authenticationError={
          providers.error ??
          (session.error instanceof ApiError && session.error.status === 401
            ? null
            : session.error)
        }
        retryAuthentication={() => {
          session.reload();
          providers.reload();
        }}
      />
    );
  if (window.location.pathname === '/verify-account')
    return <AccountVerification copy={copy} />;
  if (window.location.pathname === '/confirm')
    return <Confirmation copy={copy} language={language} />;
  const member = selected
    ? session.data?.memberships.find((item) => item.bot_id === selected)
    : session.data?.memberships[0];
  const selectedBotId = member?.bot_id ?? '';
  const requestedCompany = location.searchParams.get('companyId');
  const accountCompanyId =
    requestedCompany ??
    member?.company_id ??
    session.data?.accounts.find((account) =>
      ['owner', 'admin'].includes(account.role)
    )?.companyId ??
    '';
  const accountAdmin = session.data?.accounts.some(
    (account) =>
      account.companyId === accountCompanyId &&
      ['owner', 'admin'].includes(account.role)
  );
  const accountView = accountDestination(view, Boolean(accountAdmin));
  const botScopeValid =
    !requestedCompany || member?.company_id === requestedCompany;

  const signedOut =
    session.error instanceof ApiError && session.error.status === 401;
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <a className="brand" href="/">
          {copy.product}
        </a>
        <p className="sidebar-label">{copy.workspace}</p>
        <label className="sr-only" htmlFor="assistant-select">
          {copy.selectBot}
        </label>
        <select
          id="assistant-select"
          className="bot-select"
          value={selectedBotId}
          onChange={(event) => setSelected(event.target.value)}
        >
          <option value="">{copy.selectBot}</option>
          {session.data?.memberships.map((member) => (
            <option
              key={`${member.company_id}/${member.bot_id}`}
              value={member.bot_id}
            >
              {member.name}
            </option>
          ))}
        </select>
        {member && (
          <nav aria-label={copy.workspace}>
            {(
              [
                'home',
                'inbox',
                'leads',
                'agenda',
                ...(['owner', 'admin'].includes(member.role)
                  ? ['assistants' as const]
                  : []),
              ] as const
            ).map((item) => (
              <button
                key={item}
                className={
                  view === item ||
                  (item === 'assistants' &&
                    [
                      'configuration',
                      'knowledge',
                      'quality',
                      'readiness',
                    ].includes(view))
                    ? 'active'
                    : ''
                }
                aria-current={view === item ? 'page' : undefined}
                onClick={() => setView(item)}
              >
                {copy[item]}
              </button>
            ))}
          </nav>
        )}
        {session.data?.canCreateInstallation && (
          <button
            onClick={() =>
              void mutation.run(async () => {
                await prepareNavigation();
                setCreatingClient(true);
              })
            }
          >
            {copy.createClient}
          </button>
        )}
        <div className="sidebar-footer">
          {session.data?.accounts.some((account) =>
            ['owner', 'admin'].includes(account.role)
          ) && (
            <button
              className={accountView ? 'active' : ''}
              onClick={() => setView('accountAiProviders')}
            >
              {copy.accountSettings}
            </button>
          )}
          <button onClick={() => setView('help')}>{copy.help}</button>
          <label className="sr-only" htmlFor="language">
            {copy.locale}
          </label>
          <select
            id="language"
            value={language}
            onChange={(event) =>
              setLanguage(event.target.value === 'es' ? 'es' : 'en')
            }
          >
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
          {session.data && (
            <>
              <span className="signed-in-name">{session.data.name}</span>
              <button
                className="quiet"
                onClick={() =>
                  void mutation.run(async () => {
                    await prepareNavigation();
                    await request('/v1/logout', z.null(), {
                      csrf: session.data!.csrf,
                      body: {},
                    });
                    setSelected('');
                    session.reload();
                  })
                }
              >
                {copy.signOut}
              </button>
            </>
          )}
        </div>
      </aside>
      <main className="main-workspace">
        <header className="workspace-header">
          <span>{member?.name ?? copy.workspace}</span>
          <span className="muted">{copy[view]}</span>
        </header>
        {!signedOut && (
          <ErrorNotice
            error={session.error ?? mutation.error}
            copy={copy}
            onRetry={session.reload}
          />
        )}
        {session.loading && !session.data && (
          <p className="page-loading">{copy.loading}</p>
        )}
        {signedOut && (
          <section className="sign-in">
            <span className="product-symbol" aria-hidden="true">
              a
            </span>
            <h1>{copy.signIn}</h1>
            <ErrorNotice
              error={providers.error}
              copy={copy}
              onRetry={providers.reload}
            />
            {providers.loading && <p>{copy.loading}</p>}
            {providers.data?.providers.length === 0 && (
              <p role="alert">{copy.noIdentityProviders}</p>
            )}
            {providers.data?.providers.map((provider) => (
              <a
                key={provider}
                className="provider-button"
                href={`/v1/login/${provider}`}
              >
                {copy[provider]}
                <span>↗</span>
              </a>
            ))}
          </section>
        )}
        {session.data?.canCreateInstallation && creatingClient && (
          <ClientInstallation
            session={session.data}
            copy={copy}
            onCancel={() => setCreatingClient(false)}
            onCreated={(botId) => {
              void mutation.run(() =>
                navigateWorkspace({
                  botId,
                  companyId: null,
                  view: 'configuration',
                  step: '0',
                })
              );
              setCreatingClient(false);
              session.reload();
            }}
          />
        )}
        {session.data &&
          (!member || !botScopeValid) &&
          !creatingClient &&
          !accountView &&
          view !== 'help' && (
            <section className="workspace-empty">
              <h1>{copy.selectBot}</h1>
              {session.data.memberships.length === 0 && (
                <>
                  <p>{copy.noBots}</p>
                  <p className="muted">{copy.identityHelp}</p>
                </>
              )}
            </section>
          )}
        {session.data && accountAdmin && view !== 'connections' && (
          <CalendarReconnectNotice
            key={accountCompanyId}
            companyId={accountCompanyId}
            copy={copy}
            onOpen={() => setView('connections')}
          />
        )}
        {session.data &&
          !creatingClient &&
          accountView &&
          (accountAdmin ? (
            <AccountWorkspace
              key={accountCompanyId}
              companyId={accountCompanyId}
              selectedBotId={selected}
              session={session.data}
              view={view}
              language={language}
              onNavigate={setView}
            />
          ) : (
            <p className="page-section" role="alert">
              {copy.ACCOUNT_ACCESS_FORBIDDEN}
            </p>
          ))}
        {session.data && !creatingClient && view === 'help' && (
          <Help language={language} />
        )}
        {session.data &&
          member &&
          botScopeValid &&
          !creatingClient &&
          !accountView &&
          view !== 'help' && (
            <>
              <PlanCapacityNotice
                key={`${member.company_id}/${member.bot_id}`}
                path={botPath(
                  { companyId: member.company_id, botId: member.bot_id },
                  'capacity'
                )}
                copy={copy}
                language={language}
                onOpen={view === 'usage' ? null : () => setView('usage')}
              />
              <HandoffNotice
                scope={{ companyId: member.company_id, botId: member.bot_id }}
                copy={copy}
                visible={view !== 'handoffOperations'}
                onOpen={() => setView('handoffOperations')}
              />
              <BotWorkspace
                key={`${member.company_id}/${member.bot_id}`}
                member={member}
                session={session.data}
                view={view}
                language={language}
                onNavigate={setView}
              />
            </>
          )}
      </main>
    </div>
  );
}

function BotWorkspace({
  member,
  session,
  view,
  language,
  onNavigate,
}: {
  member: Session['memberships'][number];
  session: Session;
  view: View;
  language: Language;
  onNavigate: (view: View) => void;
}): JSX.Element {
  const scope = { companyId: member.company_id, botId: member.bot_id };
  const copy = translations[language];
  const snapshot = useRemote(botPath(scope, 'snapshot'), snapshotSchema);
  const canAdmin = ['owner', 'admin'].includes(member.role);
  const companyAdmin = session.accounts.some(
    (account) =>
      account.companyId === scope.companyId &&
      ['owner', 'admin'].includes(account.role)
  );
  const assistantViews: View[] = [
    'assistants',
    'configuration',
    'knowledge',
    'quality',
    'readiness',
  ];
  const tabs = (items: View[]) => (
    <nav className="workspace-tabs" aria-label={copy.workspaceSections}>
      {items.map((item) => (
        <button
          key={item}
          aria-current={view === item ? 'page' : undefined}
          onClick={() => onNavigate(item)}
        >
          {copy[item]}
        </button>
      ))}
    </nav>
  );
  if (view === 'home' || view === 'assistants')
    return (
      <>
        {view === 'assistants' && tabs(assistantViews)}
        <section className="settings-content">
          <div className="section-heading">
            <h1>{view === 'home' ? copy.home : member.name}</h1>
            <p>
              {view === 'home' ? copy.homeHelp : copy.assistantOverviewHelp}
            </p>
          </div>
          <ErrorNotice
            error={snapshot.error}
            copy={copy}
            onRetry={snapshot.reload}
          />
          {snapshot.loading && !snapshot.data && <p>{copy.loading}</p>}
          {snapshot.data && (
            <>
              <div className="overview-status">
                <strong>
                  {snapshot.data.release ? copy.published : copy.notPublished}
                </strong>
                <span>
                  {snapshot.data.draft
                    ? copy.configurationDraftSaved
                    : copy.noUnpublishedDraft}
                </span>
              </div>
              <div className="task-list">
                {(view === 'home'
                  ? [
                      'inbox',
                      'leads',
                      'agenda',
                      'handoffOperations',
                      ...(canAdmin ? ['configuration', 'readiness'] : []),
                    ]
                  : ['configuration', 'knowledge', 'readiness', 'quality']
                ).map((item) => (
                  <button key={item} onClick={() => onNavigate(item as View)}>
                    <strong>{copy[item as keyof typeof copy]}</strong>
                    <span aria-hidden="true">→</span>
                  </button>
                ))}
              </div>
              {!companyAdmin && (
                <details className="advanced-settings">
                  <summary>{copy.assistantAdministration}</summary>
                  <nav
                    className="workspace-tabs"
                    aria-label={copy.assistantAdministration}
                  >
                    {(
                      [
                        'connections',
                        'usage',
                        ...(canAdmin ? ['privacy'] : []),
                      ] as View[]
                    ).map((item) => (
                      <button key={item} onClick={() => onNavigate(item)}>
                        {copy[item]}
                      </button>
                    ))}
                  </nav>
                </details>
              )}
            </>
          )}
        </section>
      </>
    );
  if (view === 'privacy')
    return canAdmin ? (
      <Privacy
        scope={scope}
        session={session}
        copy={copy}
        language={language}
      />
    ) : (
      <p className="page-section" role="alert">
        {copy.privacyForbidden}
      </p>
    );
  if (view === 'handoffOperations')
    return (
      <Handoff
        scope={scope}
        session={session}
        copy={copy}
        language={language}
        canAdmin={canAdmin}
        canWrite={member.role !== 'viewer'}
      />
    );
  if (view === 'readiness')
    return (
      <Readiness
        scope={scope}
        copy={copy}
        language={language}
        onNavigate={onNavigate}
      />
    );
  if (view === 'quality')
    return (
      <Quality
        scope={scope}
        session={session}
        copy={copy}
        canWrite={member.role !== 'viewer'}
      />
    );
  if (view === 'agenda')
    return <Agenda scope={scope} copy={copy} language={language} />;
  if (view === 'inbox')
    return (
      <Inbox
        scope={scope}
        session={session}
        copy={copy}
        language={language}
        canWrite={member.role !== 'viewer'}
      />
    );
  return (
    <>
      <ErrorNotice
        error={snapshot.error}
        copy={copy}
        onRetry={snapshot.reload}
      />
      {snapshot.loading && !snapshot.data && (
        <p className="page-loading">{copy.loading}</p>
      )}
      {snapshot.data &&
        (() => {
          const props = {
            scope,
            session,
            copy,
            language,
            canAdmin,
            isOwner: member.role === 'owner',
            snapshot: snapshot.data,
            reload: snapshot.reload,
          };
          if (view === 'leads') return <Leads {...props} />;
          if (view === 'connections') return <Connections {...props} />;
          if (view === 'usage')
            return (
              <div className="settings-content">
                <Usage {...props} />
                <Billing {...props} canWrite={canAdmin} />
              </div>
            );
          if (view === 'configuration')
            return (
              <>
                {' '}
                {tabs(assistantViews)}
                <ConfigurationEditor
                  key={`${scope.companyId}/${scope.botId}`}
                  {...props}
                />
              </>
            );
          if (view === 'knowledge')
            return (
              <>
                {tabs(assistantViews)}
                <Knowledge {...props} />
              </>
            );
          return null;
        })()}
    </>
  );
}

function Help({ language }: { language: Language }): JSX.Element {
  const copy = translations[language];
  return (
    <section className="settings-content">
      <div className="section-heading">
        <h1>{copy.help}</h1>
        <p>{copy.helpIntroduction}</p>
      </div>
      {(
        ['helpSetup', 'helpDaily', 'helpAccounts', 'helpPublishing'] as const
      ).map((key) => (
        <section className="form-section" key={key}>
          <h2>{copy[key]}</h2>
          <p>{copy[`${key}Body`]}</p>
        </section>
      ))}
    </section>
  );
}
